import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiPostAuth } from "../utils/api"; // uses your auth wrapper
import RobitoLogo from "../components/AppLogo";

/** Retry settings */
const MAX_TRIES = 10;         // total attempts including the first one
const INTERVAL_MS = 3000;     // 3s between attempts

export default function PurchaseSuccess() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [failMsg, setFailMsg] = useState("");
  const [tries, setTries] = useState(0);

  const timerRef = useRef(null);
  const unmountedRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const redirectToSite = (siteId) => {
    // Replace so user cannot go back to this waiting page
    navigate(`/dashboard/site/${encodeURIComponent(siteId)}`, { replace: true });
  };

  const checkLatestSite = async () => {
    if (unmountedRef.current) return;

    setLoading(true);
    setFailMsg("");

    try {
      const response = await apiPostAuth("/site/get_latest_site.php");
      // Expected shape: { result: 1, data: { site_id: "..." }, message?: string }
      console.log(response)
      if (response?.result === 1 && response?.site_id) {
        redirectToSite(response.site_id);
        return;
      }

      // Not found yet — set message but keep trying if we have attempts left
      setFailMsg(response?.message || "Site belum ditemukan. Menguji kembali…");
    } catch (err) {
      setFailMsg("Tidak dapat memeriksa status pembelian saat ini.");
    } finally {
      setLoading(false);
      setTries((t) => t + 1);
    }
  };

  // Initial + auto-retry
  useEffect(() => {
    unmountedRef.current = false;

    const run = async () => {
      await checkLatestSite();
      // schedule next attempt if not redirected and attempts remain
      if (!unmountedRef.current) {
        timerRef.current = setTimeout(function schedule() {
          if (unmountedRef.current) return;
          if (tries + 1 < MAX_TRIES) {
            checkLatestSite();
            timerRef.current = setTimeout(schedule, INTERVAL_MS);
          }
        }, INTERVAL_MS);
      }
    };

    run();

    return () => {
      unmountedRef.current = true;
      clearTimer();
    };
    // We intentionally don't include `tries` here to avoid re-creating the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const manualRetry = () => {
    clearTimer();
    setFailMsg("");
    setLoading(true);
    checkLatestSite();
  };

  return (
    <div className="app-shell">
      <header className="header">
        <div className="left" />
        <div className="logo">
          <RobitoLogo />
        </div>
        <div className="right" />
      </header>

      <main className="main">
        <div className="summary-card" style={{ maxWidth: 520, margin: "0 auto" }}>
          <h3 style={{ marginTop: 0 }}>Memeriksa pembelian kamu…</h3>
          <p className="small" style={{ marginTop: 6 }}>
            Kami sedang mengecek apakah website barumu sudah aktif.
          </p>

          <div
            className="small"
            style={{ marginTop: 12, opacity: 0.9 }}
            aria-live="polite"
          >
            Percobaan: {Math.min(tries + 1, MAX_TRIES)} / {MAX_TRIES}
          </div>

          {failMsg && (
            <div className="small" style={{ marginTop: 10, color: "#ffb3b3" }}>
              {failMsg}
            </div>
          )}

          <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
            <button
              className="btn block pay-cta"
              onClick={manualRetry}
              disabled={loading}
            >
              {loading ? "Memeriksa…" : "Coba Periksa Lagi"}
            </button>

            <button
              className="btn block"
              onClick={() => navigate("/dashboard", { replace: true })}
              style={{
                background: "transparent",
                color: "var(--text)",
                border: "1px solid var(--border)"
              }}
              disabled={loading}
            >
              Kembali ke Dashboard
            </button>
          </div>

          <p className="small" style={{ marginTop: 10, color: "var(--muted)" }}>
            Halaman ini akan otomatis mengarahkan kamu ke website baru saat sudah siap.
          </p>
        </div>
      </main>
    </div>
  );
}
